/**
 * Manual (operator-agreed) per-kg rates in the New Order wizard.
 *
 * Mirrors backend/src/utils/manual-price.js so the operator sees the same
 * eligibility and the same validation message before the round trip. The backend
 * remains the authority: it re-validates the rate, re-computes the line total,
 * and ignores an override on an ineligible line. Nothing here is trusted for
 * money - the preview exists so the counter staff can see what they are agreeing
 * to, not to decide what gets charged.
 */

/** Services whose by-weight lines accept an operator-agreed rate. */
const MANUAL_PRICE_SERVICE_NAMES = [
  'wash & fold', 'wash and fold',
  'wash & iron', 'wash and iron',
]

const MAX_MANUAL_RATE = 100000

export const isWeightUnit = (unit?: string | null) =>
  String(unit || '').toLowerCase() === 'per_kg'

/**
 * Only by-weight lines of the two allow-listed services. The frontend matches on
 * service NAME because that is all the catalog payload carries; the backend
 * matches on the (rename-safe) slug and has the final say.
 */
export const isManualPriceEligible = (item: {
  unit?: string | null
  service_name?: string | null
}) => {
  if (!isWeightUnit(item.unit)) return false
  return MANUAL_PRICE_SERVICE_NAMES.includes(String(item.service_name || '').trim().toLowerCase())
}

export type RateValidation =
  | { ok: true; value: number }
  | { ok: false; message: string }

/**
 * Validate an operator-entered rate. Rejects blank, non-numeric, NaN, Infinity
 * and negative values; allows decimals and zero (a free redo is a real case).
 */
export const validateManualRate = (raw: string): RateValidation => {
  const trimmed = String(raw ?? '').trim()
  if (!trimmed) return { ok: false, message: 'Enter a rate.' }
  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return { ok: false, message: 'Rate must be a number.' }
  if (parsed < 0) return { ok: false, message: 'Rate cannot be negative.' }
  if (parsed > MAX_MANUAL_RATE) return { ok: false, message: 'Rate is unrealistically high.' }
  return { ok: true, value: Math.round(parsed * 100) / 100 }
}

/**
 * The rate this line will actually be charged at, for preview only.
 * Falls back to the catalog rate whenever the manual draft is absent or invalid,
 * so a half-typed rate never makes the running total nonsense.
 */
export const effectiveUnitPrice = (
  item: { id: string; price: number | string; unit?: string | null; service_name?: string | null },
  manualDrafts: Record<string, string>,
): number => {
  const catalog = Number(item.price || 0)
  if (!isManualPriceEligible(item)) return catalog
  const draft = manualDrafts[item.id]
  if (draft === undefined) return catalog
  const parsed = validateManualRate(draft)
  return parsed.ok ? parsed.value : catalog
}

/** Line total for the preview: quantity × effective rate, at 2dp. */
export const previewLineTotal = (
  item: { id: string; price: number | string; unit?: string | null; service_name?: string | null },
  quantity: number,
  manualDrafts: Record<string, string>,
): number => Math.round(effectiveUnitPrice(item, manualDrafts) * quantity * 100) / 100

/**
 * Every manual draft that is present but invalid, keyed by item id. A non-empty
 * result must block submission - otherwise the operator's intended rate would be
 * silently dropped and the order priced at the catalog rate instead.
 */
export const collectManualRateErrors = (
  items: Array<{ id: string; name: string; unit?: string | null; service_name?: string | null }>,
  manualDrafts: Record<string, string>,
): Record<string, string> => {
  const errors: Record<string, string> = {}
  for (const item of items) {
    const draft = manualDrafts[item.id]
    if (draft === undefined) continue
    if (!isManualPriceEligible(item)) continue
    const parsed = validateManualRate(draft)
    if (!parsed.ok) errors[item.id] = parsed.message
  }
  return errors
}

export { MAX_MANUAL_RATE, MANUAL_PRICE_SERVICE_NAMES }
